(() => {
  const copy = JSON.parse(document.querySelector('[data-joa-native-copy]')?.textContent || '{}');
  const text = key => copy[key] || key;
  const group = item => String(item.properties?._mws_cart || item.properties?.__mws_apo_cart || '');
  const route = suffix => `${window.Shopify?.routes?.root || '/'}cart${suffix}`;
  const sections = () => [...new Set([...document.querySelectorAll('cart-items-component')].map(node => node.dataset.sectionId).filter(Boolean))];
  async function request(url, body) {
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json', ...(body && !(body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}) }, ...(body ? { method: 'POST', body: body instanceof FormData ? body : JSON.stringify(body) } : {}) });
    const data = await response.json();
    if (!response.ok || data.status >= 400 || data.errors) throw new Error(data.description || data.message || text('error'));
    return data;
  }
  async function refresh(cart, action = 'update') {
    if (window.mwApoInit) window.mwApoInit.cart = cart;
    const { CartLinesUpdateEvent } = await import('@shopify/events');
    document.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines: [], promise: Promise.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { source: 'joa-cart-actions', sections: cart.sections, items: cart.items, itemCount: cart.item_count, didError: false } }) }));
  }
  const cart = () => request(route('.js?app=mws_apo'));
  async function payment(key, plan) {
    const snapshot = await cart();
    const index = snapshot.items.findIndex(item => item.key === key);
    if (index < 0) throw new Error(text('changed'));
    const result = await request(route('/change.js?app=mws_apo'), { line: index + 1, quantity: snapshot.items[index].quantity, selling_plan: plan ? Number(plan) : null, sections: sections(), sections_url: location.pathname });
    await refresh(result);
    return result;
  }
  async function replace(state, data) {
    const snapshot = await cart();
    const original = snapshot.items.find(item => item.key === state.cartEdit.key);
    if (!original || original.quantity !== state.cartEdit.quantity) throw new Error(text('changed'));
    const token = crypto.randomUUID();
    data.set('properties[_joa_cart_edit]', token);
    data.set('quantity', String(original.quantity));
    data.delete('sections');
    let replacement;
    try {
      await request(route('/add.js?app=mws_apo'), data);
      let afterAdd = await cart();
      replacement = afterAdd.items.find(item => item.properties?._joa_cart_edit === token);
      const old = afterAdd.items.find(item => item.key === original.key);
      if (!replacement || !old || old.quantity !== original.quantity || replacement.quantity !== original.quantity) throw new Error(text('changed'));
      if (replacement.properties?._joa_birthstone_pricing !== 'included' && window.JoaBirthstones?.valid(replacement.properties?._joa_birthstone_ids).length) {
        const child = window.JoaBirthstones.childFor({ ...replacement, selling_plan: data.get('selling_plan') }, replacement.properties);
        if (!child) throw new Error(text('error'));
        delete child.parent_id;
        child.parent_line_key = replacement.key;
        await request(route('/add.js?app=mws_apo'), { items: [child] });
        afterAdd = await cart();
        const attached = afterAdd.items.filter(item => item.parent_relationship?.parent_key === replacement.key && item.properties?._joa_birthstone_addon && Number(item.id) === child.id);
        if (attached.reduce((count, item) => count + item.quantity, 0) !== child.quantity) throw new Error(text('changed'));
        if (afterAdd.items.find(item => item.key === old.key)?.quantity !== original.quantity) throw new Error(text('changed'));
      }
      const updates = { [old.key]: 0 };
      const oldGroup = group(old);
      const parents = afterAdd.items.filter(item => oldGroup && group(item) === oldGroup && !item.properties?._mws_option);
      const total = parents.reduce((sum, item) => sum + item.quantity, 0);
      for (const child of afterAdd.items) {
        // Shopify removes nested add-ons with their parent. Child keys can be
        // identical across the old and replacement parents, so never delete
        // a nested child by key during replacement or rollback.
        if (!child.parent_relationship && oldGroup && group(child) === oldGroup && child.properties?._mws_option) {
          const quantity = child.quantity * (total - old.quantity) / total;
          if (!Number.isInteger(quantity)) throw new Error(text('changed'));
          updates[child.key] = quantity;
        }
      }
      const result = await request(route('/update.js?app=mws_apo'), { updates, sections: sections(), sections_url: location.pathname });
      await refresh(result);
      return result;
    } catch (error) {
      // A failed response can follow a successful server write. Inspect once
      // before rolling back so the customer's only copy is never removed.
      const latest = await cart();
      const old = latest.items.find(item => item.key === original.key);
      replacement = latest.items.find(item => item.properties?._joa_cart_edit === token);
      if (!old && replacement) { await refresh(latest); return latest; }
      if (old && replacement) {
        const rollback = { [replacement.key]: 0 };
        latest.items.forEach(item => { if (!item.parent_relationship && group(replacement) && group(replacement) !== group(old) && group(item) === group(replacement)) rollback[item.key] = 0; });
        await request(route('/update.js?app=mws_apo'), { updates: rollback });
      }
      throw error;
    }
  }
  async function showEditor(editor, button) {
    const drawer = button?.closest('theme-drawer');
    if (drawer?.isOpen) {
      await drawer.close();
      editor.addEventListener('close', () => {
        const host = document.getElementById(drawer.id);
        if (!host?.isConnected) return;
        host.open();
        if (button.isConnected) button.focus({ preventScroll: true });
      }, { once: true });
    }
    if (!editor.open) editor.showModal();
  }
  window.JoaCartActions = { cart, payment, replace, refresh, showEditor };

  let dialog, saving = false, opening = false, trigger;
  document.addEventListener('click', async event => {
    const button = event.target.closest?.('[data-joa-payment-edit]');
    if (!button) return;
    const row = button.closest('[data-key]');
    const template = row?.querySelector('[data-joa-cart-payment-template]');
    if (!template || saving || opening) return;
    event.preventDefault(); event.stopImmediatePropagation(); trigger = button;
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.className = 'joa-native-editor joa-config-modal joa-cart-payment-dialog';
      dialog.setAttribute('aria-labelledby', 'JoaCartPaymentTitle');
      dialog.innerHTML = '<div class="joa-cart-editor-header"><h2 id="JoaCartPaymentTitle"></h2><button type="button" class="joa-native-editor__close">×</button></div><div class="joa-native-editor__body"></div><div class="joa-native-editor__footer"><p role="status" class="joa-native-editor__status"></p><button type="button" class="button joa-native-editor__save"></button></div>';
      document.body.append(dialog);
      dialog.querySelector('h2').textContent = text('payment');
      const close = dialog.querySelector('.joa-native-editor__close');
      close.setAttribute('aria-label', text('close'));
      close.addEventListener('click', () => { if (!saving) dialog.close(); });
      dialog.addEventListener('cancel', event => { if (saving) event.preventDefault(); });
      dialog.addEventListener('close', () => { document.body.classList.remove('joa-native-editor-open'); trigger?.focus({ preventScroll: true }); });
      dialog.querySelector('.joa-native-editor__save').addEventListener('click', async () => {
        if (saving) return;
        const status = dialog.querySelector('[role="status"], [role="alert"]');
        const panel = dialog.querySelector('fieldset');
        const selected = panel.querySelector('input:checked');
        if (!selected) return;
        saving = true; panel.disabled = true;
        dialog.querySelector('.joa-native-editor__save').disabled = true;
        status.setAttribute('role', 'status'); status.textContent = text('saving');
        try { await payment(dialog.dataset.lineKey, selected.value); dialog.close(); }
        catch (error) { status.setAttribute('role', 'alert'); status.textContent = error.message || text('error'); }
        finally { saving = false; panel.disabled = false; dialog.querySelector('.joa-native-editor__save').disabled = false; }
      });
    }
    dialog.dataset.lineKey = row.dataset.key;
    dialog.querySelector('.joa-native-editor__body').replaceChildren(template.content.cloneNode(true));
    const panel = dialog.querySelector('[data-joa-editor-payment]');
    const edit = row.querySelector('[data-joa-birthstones]');
    const properties = JSON.parse(edit?.dataset.joaBirthstones || '{}');
    const extra = (properties._joa_birthstone_pricing === 'included' ? 0 : (window.JoaBirthstones?.valid(properties._joa_birthstone_ids).length || 0)) * Number(edit?.dataset.joaBirthstoneUnit || 0);
    const base = Number(panel.dataset.fullPrice);
    if (Number.isFinite(base) && extra) {
      const money = { format: amount => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', useGrouping: Math.abs(amount) >= 10000, currency: panel.dataset.currency }).format(amount) };
      const format = cents => `${money.format(cents / 100)} ${panel.dataset.currency}`;
      panel.querySelector('[data-joa-editor-full]').textContent = format(base + extra);
      panel.querySelectorAll('[data-deposit-rate]').forEach(input => {
        const rate = Number(input.dataset.depositRate);
        const due = Math.round(base * rate) + ((properties._joa_birthstone_plans || '').split(',').includes(input.value) ? Math.round(extra * rate) : extra);
        input.closest('label').querySelector('[data-joa-editor-now]').textContent = `${format(due)} ${panel.dataset.now}`;
        input.closest('label').querySelector('[data-joa-editor-later]').textContent = `${format(base + extra - due)} ${panel.dataset.later}`;
      });
    }
    dialog.querySelector('.joa-native-editor__status').textContent = '';
    dialog.querySelector('.joa-native-editor__save').textContent = text('save');
    window.JoaOptionDesign?.styleModalPayment(dialog);
    opening = true;
    try {
      await showEditor(dialog, button);
      document.body.classList.add('joa-native-editor-open');
    } finally { opening = false; }
  }, true);
})();


